import axios, { type AxiosError } from 'axios';
import { omit } from 'lodash';

import useLocalStorage from '~hooks/useLocalStorage';
import { initializeAxiosInterceptors } from './axiosConfig';

type RequestProps<Response, Data, Error> = {
  type: 'private' | 'public';
  url: string;
  extraConfig?: Record<string, unknown>;
  thenFn: (res: Response) => void;
  catchFn?: (error?: Error) => void;
  finallyFn?: () => void;
} & (
  | { method: 'GET'; params?: Data }
  | { method: 'PUT'; params?: Data; data?: Data }
  | { method: 'POST' | 'PATCH' | 'DELETE'; data?: Data }
);

export const doRequest = async <Response, Data, Error>(
  props: RequestProps<Response, Data, Error>
) => {
  initializeAxiosInterceptors();

  const { type, url, method, extraConfig, thenFn, catchFn, finallyFn } = props;
  const { userData } = useLocalStorage();

  const token = {
    private: `Bearer ${userData?.access}`,
    public: `Token ${import.meta.env.VITE_PUBLIC_TOKEN}`
  }[type];

  const config = {
    baseURL: import.meta.env.VITE_BACKEND_URL,
    url,
    method,
    _type: type,
    ...extraConfig
  };

  if (method === 'GET' || method === 'PUT') {
    Object.assign(config, { params: props.params });
  } else Object.assign(config, { data: props.data });

  if (type === 'private') {
    Object.assign(config, { headers: { Authorization: token } });
  } else if (type === 'public') {
    Object.assign(config, {
      headers: { recaptcha: localStorage.getItem('recaptcha') }
    });
  }

  await axios(config)
    .then((res) => thenFn(res.data as Response))
    .catch((error: AxiosError<Error>) => catchFn?.(error?.response?.data))
    .finally(() => finallyFn?.());
};

export const downloadServerFile = (data: BlobPart, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatTableParams = (params: PaginatedParams) => {
  const length = params.length ?? 0;
  const page = params.page ?? 0;

  const finalParams = omit(
    {
      format: 'datatables',
      start: length * page,
      ...params
    },
    ['page']
  );

  return finalParams;
};
